import React, { FC, Fragment, useMemo } from 'react';
import {
    FormFieldActionEvent,
    JAvatarProps,
    JCheckboxGroupProps,
    JDateProps,
    JFieldBaseProps,
    JPrivacyProps,
    JRadioProps,
    JUploadProps,
    JValueSelectProps
} from '../models';
import { JFieldDTO } from '../models/overrideBE';
import { cmsOptionsToRadioOptions, cmsOptionsToSelectOptions, isFieldType, isGenericDateField, isGenericDateRangeField, transformMinMaxDateParam } from './../utils';
import { useCompositorContext } from './CompositorProvider';
import * as Fields from './Fields';


type MapFieldComponentProps = {
    formItemName: (string | number)[],
    fullname?: (string | number)[],
    field: JFieldDTO,
    disabled?: boolean,
    onAction: (event: FormFieldActionEvent) => void
}
function getFieldValue(field: JFieldDTO) {
    // If the field has an override value, we use it
    let values = field?.overrideValue ? [field?.overrideValue] : (field.values?.length ? field.values : field.cmsConfig.defaultValues)
    const type = field.type
    switch (true) {
        case isFieldType.AVATAR(type):
        case isFieldType.FILE(type):
            return field.values?.filter(v => !!v)
        case isGenericDateRangeField(type):
            return [values?.[0] || '', values?.[1] || '']
        case isFieldType.CHECKBOX(type):
        case isFieldType.MULTISELECT(type):
            return values?.length ? values : []

        default:
            return values?.length ? values[0] : undefined
    }
}
export const MapFieldComponent: FC<MapFieldComponentProps> = ({ field, formItemName, onAction, fullname, disabled }) => {
    const { showFieldsLabel } = useCompositorContext()
    const genericProps: JFieldBaseProps = useMemo(() => ({
        type: field.type,
        name: formItemName,
        fullname: fullname || formItemName,
        fieldKey: field.hurlId,
        label: field.cmsConfig.title || '',
        placeholder: !!field.overrideValue ? (field.cmsConfig.placeholder ?? field.cmsConfig.text ?? '') : ( field.cmsConfig.placeholder ?? field.cmsConfig.text ?? field.cmsConfig.title ?? ''),
        tooltip: field.cmsConfig.tooltip,
        description: field.cmsConfig.additionalText,
        infoFileURL: field.cmsConfig.imageUrl,
        hidden: field.cmsConfig.hidden,
        showLabel: showFieldsLabel,
        mandatory: field.mandatory,
        confirmed: field.confirmed,
        validationScript: field.validationFunction,
        validationMessage: field.cmsConfig.validationMessage,
        value: getFieldValue(field),
        disabled: disabled || field.readOnly,
        overrideValue: field.overrideValue,
        extraOptions: field.cmsConfig.componentConfig?.options as unknown as Record<string, string>,
        autocomplete: field.cmsConfig?.autocomplete,
        onAction
    }), [field])


    const type = field?.type
    switch (true) {
        case isFieldType.TEXT(type):
            return (
                <Fields.JText
                    {...genericProps}
                    status={field.cmsConfig.componentConfig?.status}
                />
            );

        case isFieldType.PHONE(type):
            return (
                <Fields.JPhone {...genericProps} />
            );
        case isFieldType.COUNTRY(type):
            return (
                <Fields.JCountry {...genericProps} />
            );

        case isFieldType.SELECT(type):
            const jValueSelectProps: JValueSelectProps = {
                ...genericProps,
                options: cmsOptionsToSelectOptions(field.cmsConfig.componentConfig?.options),
                searcheable: field.cmsConfig.componentConfig.searcheable,
                disableAllowClear: field.cmsConfig.componentConfig?.disableAllowClear,
                dependsOn: field.cmsConfig.componentConfig?.dependsOn
            }
            return (
                <Fields.JValueSelect {...jValueSelectProps} />
            )

        case isFieldType.MULTISELECT(type):
            const jValueMultiSelectProps: JValueSelectProps = {
                ...genericProps,
                options: cmsOptionsToSelectOptions(field.cmsConfig.componentConfig?.options),
                disableAllowClear: field.cmsConfig.componentConfig?.disableAllowClear,
                dependsOn: field.cmsConfig.componentConfig?.dependsOn
            }
            return (
                <Fields.JValueSelect {...jValueMultiSelectProps} mode={'multiple'} />
            )

        case isFieldType.NUMBER(type):
            return (<Fields.JNumber {...genericProps} />);

        case isFieldType.EMAIL(type):
            return (<Fields.JMail {...genericProps} />);

        case isFieldType.PASSWORD(type):
            return (<Fields.JPassword {...genericProps} />);

        case isFieldType.RADIO(type):
            const jRadioProps: JRadioProps = {
                ...genericProps,
                overrideValue: field.overrideValue,
                style: field.cmsConfig.componentConfig?.radioStyle,
                placeholder: !showFieldsLabel && !field.cmsConfig.text ? '' : genericProps.placeholder,
                options: cmsOptionsToRadioOptions(field.cmsConfig.componentConfig?.options)
            }
            return (<Fields.JRadio {...jRadioProps} />);

        case isFieldType.CHECKBOX(type):
            const jCheckboxProps: JCheckboxGroupProps = {
                ...genericProps,
                value: genericProps.value as string[],
                overrideValue: field.overrideValue,
                placeholder: !showFieldsLabel && !field.cmsConfig.text ? '' : genericProps.placeholder,
                options: cmsOptionsToRadioOptions(field.cmsConfig.componentConfig?.options)
            }
            return (<Fields.JCheckboxGroup {...jCheckboxProps} />);


        case isGenericDateField(type):

            const minDate = field.cmsConfig.componentConfig?.minDate;
            const maxDate = field.cmsConfig.componentConfig?.maxDate;

            const genericDateProps: JDateProps = {
                ...genericProps,
                minDate: transformMinMaxDateParam(minDate),
                maxDate: transformMinMaxDateParam(maxDate),
                showTime: field?.cmsConfig?.componentConfig?.showTime !== false,
                value: genericProps.value as string
            }
            if (isFieldType.DATE(type)) {
                return (
                    <Fields.JDate
                        {...genericDateProps}
                        showTime={false}
                    />
                );
            }
            if (isFieldType.DATETIME(type)) {
                return (
                    <Fields.JDateTime
                        {...genericDateProps}
                    />
                );
            }
            if (isGenericDateRangeField(type)) {
                if (isFieldType.DATES_RANGE(type)) {
                    return (
                        <Fields.JDateTimeRange
                            {...genericDateProps}
                            value={genericProps.value as [string, string]}
                        />
                    );
                }
                return (
                    <Fields.JDatesRange
                        {...genericDateProps}
                        value={genericProps.value as [string, string]}
                        showTime={false}
                    />
                )
            }
            return <Fragment />

        case isFieldType.AVATAR(type):
            const JAvatarProps: JAvatarProps = useMemo(() => ({
                ...genericProps,
                multiple: false,
                maxCount: 1,
                maxSize: field.cmsConfig.componentConfig?.maxSize,
                cropRenderMode: field.cmsConfig.componentConfig?.cropRenderMode ?? 'modal',
                showPreview: !field.cmsConfig.componentConfig?.hidePreview,
                minimal: field.cmsConfig.componentConfig?.minimal,
                showAvatarPreview: field.cmsConfig.componentConfig?.showAvatarPreview,
                accept: 'image/png, image/jpeg',
                enableCrop: true,
                value: genericProps.value as string[]
            }), [])

            return (<Fields.JAvatar {...JAvatarProps} />);
        case isFieldType.FILE(type):
            const JUploadProps: JUploadProps = useMemo(() => ({
                ...genericProps,
                accept: field.cmsConfig.componentConfig?.accept,
                multiple: field.cmsConfig.componentConfig?.multiple,
                maxCount: field.cmsConfig.componentConfig?.maxCount,
                maxSize: field.cmsConfig.componentConfig?.maxSize,
                cropRenderMode: field.cmsConfig.componentConfig?.cropRenderMode ?? 'modal',
                showPreview: !field.cmsConfig.componentConfig?.hidePreview,
                enableCrop: field.cmsConfig.componentConfig?.enableCrop,
                minimal: field.cmsConfig.componentConfig?.minimal,
                value: genericProps.value as string[]
            }), [])

            return (<Fields.JUpload {...JUploadProps} />);


        case isFieldType.PRIVACY(type):
            const jPrivacyGroupProps: JPrivacyProps = {
                ...genericProps,
                options: cmsOptionsToRadioOptions(field.cmsConfig.componentConfig?.options)
            }
            return (<Fields.JPrivacy  {...jPrivacyGroupProps} />);

        case isFieldType.TEXTAREA(type):
            return <Fields.JTextArea {...genericProps} />;

        default:
            console.warn(`[FORM_COMPOSITOR]: Field type "${type}" not found.`);
            return <div></div>

    }

}